import React, { useState, useEffect } from 'react'
import load from "../assets/Images/loader.gif";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const hiddenDetailKeys = new Set(["documentbody"]);

const annotationDetailsStyles = `
.annotation-details-shell {
    padding: 18px 10px 24px;
}

.annotation-details-titlebar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 14px;
    margin-bottom: 18px;
    border-bottom: 1px solid #edf0f3;
}

.annotation-details-title {
    margin: 0;
    color: #2f2438;
    font-size: 22px;
    font-weight: 600;
}

.annotation-details-subtitle {
    margin: 6px 0 0;
    color: #6c757d;
    font-size: 14px;
    word-break: break-word;
}

.annotation-count-pill {
    flex: 0 0 auto;
    padding: 6px 10px;
    border: 1px solid #e4d9ec;
    border-radius: 4px;
    background: #fbf9fd;
    color: #5d3b72;
    font-size: 13px;
    font-weight: 600;
}

.annotation-list {
    display: grid;
    gap: 16px;
}

.annotation-item {
    border: 1px solid #e8e3ee;
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;
}

.annotation-item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px 16px;
    background: #fbf9fd;
    border-bottom: 1px solid #eee8f3;
}

.annotation-item-title {
    margin: 0;
    color: #242124;
    font-size: 16px;
    font-weight: 600;
}

.annotation-download-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 36px;
    padding: 8px 14px;
    border: 0;
    border-radius: 4px;
    background: var(--bg-color, #9465aa);
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
}

.annotation-download-btn:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.annotation-download-btn:not(:disabled):hover {
    background: #7f5297;
}

.annotation-detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.annotation-detail-row {
    display: grid;
    grid-template-columns: minmax(120px, 36%) minmax(0, 1fr);
    gap: 12px;
    padding: 14px 16px;
    border-top: 1px solid #f2f0f4;
}

.annotation-detail-row:nth-child(-n + 2) {
    border-top: 0;
}

.annotation-detail-row:nth-child(odd) {
    border-right: 1px solid #f2f0f4;
}

.annotation-detail-label {
    color: #6c757d;
    font-size: 13px;
    font-weight: 600;
}

.annotation-detail-value {
    color: #212529;
    font-size: 14px;
    font-weight: 500;
    overflow-wrap: anywhere;
}

.annotation-empty-state {
    padding: 34px 16px;
    border: 1px dashed #d7d1dc;
    border-radius: 8px;
    color: #6c757d;
    text-align: center;
}

.annotation-empty-state i {
    display: block;
    margin-bottom: 10px;
    color: var(--bg-color, #9465aa);
    font-size: 26px;
}

@media (max-width: 768px) {
    .annotation-details-titlebar,
    .annotation-item-header {
        flex-direction: column;
        align-items: stretch;
    }

    .annotation-count-pill {
        width: fit-content;
    }

    .annotation-detail-grid {
        grid-template-columns: 1fr;
    }

    .annotation-detail-row,
    .annotation-detail-row:nth-child(-n + 2),
    .annotation-detail-row:nth-child(odd) {
        grid-template-columns: 1fr;
        gap: 6px;
        border-right: 0;
        border-top: 1px solid #f2f0f4;
    }

    .annotation-detail-row:first-child {
        border-top: 0;
    }

    .annotation-download-btn {
        width: 100%;
    }
}
`;

const toastOptions = {
    position: toast.POSITION.BOTTOM_CENTER,
    autoClose: 1500,
};

const formatLabel = (key) => {
    return key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/\bId\b/g, "ID")
        .replace(/\bGuid\b/g, "GUID")
        .replace(/\bMime\b/g, "MIME");
};

const formatDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDetailValue = (key, value) => {
    if (value === null || value === undefined || value === "") {
        return "N/A";
    }

    if (["createdon", "modifiedon"].includes(key.toLowerCase())) {
        return formatDate(value);
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return value;
};

const getBase64Content = (documentBody) => {
    const body = String(documentBody || "").trim();
    const base64Marker = ";base64,";

    if (body.includes(base64Marker)) {
        return body.substring(body.indexOf(base64Marker) + base64Marker.length).replace(/\s/g, "");
    }

    return body.replace(/^data:.*,/i, "").replace(/\s/g, "");
};


const AnnotationDetails = () => {
    sessionStorage.setItem("userEmail", "crm.support@spectra.co")
    sessionStorage.setItem("userName", "crm.support")
    sessionStorage.setItem("password", "Spectra")

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const annotationId = searchParams.get("id") || null;


    const [loading, setLoading] = useState(false);
    const [annotationDetails, setAnnotationDetails] = useState([]);

    const annotations = Array.isArray(annotationDetails)
        ? annotationDetails
        : annotationDetails
            ? [annotationDetails]
            : [];

    const handleDownload = (annotation) => {
        try {
            if (!annotation?.DocumentBody) {
                toast.warning("Document is not available for download.", toastOptions);
                return;
            }

            const base64Content = getBase64Content(annotation.DocumentBody);

            if (!base64Content) {
                toast.warning("Document is not available for download.", toastOptions);
                return;
            }

            const byteCharacters = window.atob(base64Content);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
                type: annotation?.MimeType || "application/octet-stream",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = annotation?.FileName || `annotation-${annotation?.AnnotationId || Date.now()}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success("Download started.", toastOptions);
        } catch (error) {
            console.error("Error downloading file:", error);
            toast.error("Unable to download file.", toastOptions);
        }
    };

    useEffect(() => {
        const getAnnotationDetails = async () => {
            try {
                if (!annotationId) return;

                setLoading(true);

                const apiUrl = `${process.env.REACT_APP_UNIFIED_API_URL}/getGuidDetails`;

                const requestData =  { annotationId: annotationId };

                // const basicAuth = btoa("dkyc.caf:Dkyc@1234!@#$%");
                const basicAuth = btoa(`${process.env.REACT_APP_UNIFIED_API_USER}:${process.env.REACT_APP_UNIFIED_API_PASS}`);
                // console.log(`${process.env.REACT_APP_UNIFIED_API_USER}:${process.env.REACT_APP_UNIFIED_API_PASS}`);

                const response = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${basicAuth}`,
                    },
                    body: JSON.stringify(requestData),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const result = await response.json();
                console.log("API Response:", result);

                result?.meta?.code === 200 && setAnnotationDetails(result?.data || []);
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Error fetching annotation details.", toastOptions);
            } finally {
                setLoading(false);
            }
        };

        getAnnotationDetails();
    }, [annotationId]);



    return (
        <div>
            <style>{annotationDetailsStyles}</style>

            <div className="container-fluid text-light Header-style header">
                <div className="p-3">
                    <span
                        className=""
                        style={{ cursor: "pointer" }}
                    // onClick={redirectBroadbandCafformPage}
                    >
                        {/* <i className="fa-solid fa-arrow-left-long"></i> */}
                    </span>
                    <span className="p-1 m-3">Annotation Details</span>
                </div>
            </div>

            {loading && (
                <div className="loader-wrapper">
                    <div className="loader">
                        <img src={load} alt="loading...."></img>
                    </div>
                </div>
            )}

            <div
                className=" container shadow mb-3 bg-body rounded mt-4"
                id="form1"
            >
                {/* ********* GUID Details ******** */}
                <div className="container-fluid form-container">
                    <div className="annotation-details-shell">
                        <div className="annotation-details-titlebar">
                            <div>
                                <h2 className="annotation-details-title">GUID Details</h2>
                                <p className="annotation-details-subtitle">
                                    {annotationId
                                        ? `Showing details for ${annotationId}`
                                        : "Annotation ID is missing from the page URL."}
                                </p>
                            </div>

                            {annotations.length > 0 && (
                                <span className="annotation-count-pill">
                                    {annotations.length} {annotations.length === 1 ? "file" : "files"}
                                </span>
                            )}
                        </div>

                        {!annotationId && (
                            <div className="annotation-empty-state">
                                <i className="fa-regular fa-circle-question"></i>
                                Annotation ID is required to fetch details.
                            </div>
                        )}

                        {annotationId && !loading && annotations.length === 0 && (
                            <div className="annotation-empty-state">
                                <i className="fa-regular fa-file-lines"></i>
                                No annotation details found.
                            </div>
                        )}

                        {annotations.length > 0 && (
                            <div className="annotation-list">
                                {annotations.map((annotation, index) => {
                                    const detailEntries = Object.entries(annotation || {}).filter(
                                        ([key]) => !hiddenDetailKeys.has(key.toLowerCase())
                                    );

                                    return (
                                        <div
                                            className="annotation-item"
                                            key={annotation?.AnnotationId || `${annotation?.FileName || "annotation"}-${index}`}
                                        >
                                            <div className="annotation-item-header">
                                                <h3 className="annotation-item-title">
                                                    Attachment {index + 1}
                                                </h3>

                                                <button
                                                    type="button"
                                                    className="annotation-download-btn"
                                                    disabled={!annotation?.DocumentBody}
                                                    onClick={() => handleDownload(annotation)}
                                                >
                                                    <i className="fa-solid fa-download"></i>
                                                    Download
                                                </button>
                                            </div>

                                            <div className="annotation-detail-grid">
                                                {detailEntries.map(([key, value]) => (
                                                    <div className="annotation-detail-row" key={key}>
                                                        <span className="annotation-detail-label">
                                                            {formatLabel(key)}
                                                        </span>
                                                        <span className="annotation-detail-value">
                                                            {formatDetailValue(key, value)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </div>
    )
}

export default AnnotationDetails
